import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U3120";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  FINDCOUNTRIES: string;
  FINDSTATES: string;
  FINDCONTACTTYPES: string;
  FINDEMPLOYEES: string;
  FINDCUSTOMER: string;
  EXPORT_EXCEL: string;
  CREATEQUICKCUSTOMER: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  FINDCOUNTRIES: `${LOGICALUNIT_URL}/findCountries`,
  FINDSTATES: `${LOGICALUNIT_URL}/findStates`,
  FINDCONTACTTYPES: `${LOGICALUNIT_URL}/findContactTypes`,
  FINDEMPLOYEES: `${LOGICALUNIT_URL}/findEmployees`,
  FINDCUSTOMER: `${LOGICALUNIT_URL}/findCustomer`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
  CREATEQUICKCUSTOMER: `${LOGICALUNIT_URL}/createQuickCustomer`,
};

export interface U3120ContactType {
  id: string;
  contactTypeCode?: string;
  contactTypeNameFr?: string;
  contactTypeNameEn?: string;
}

/** Type de téléphone pour un contact client. */
export const U3120ContactPhoneType = {
  OFFICE: "OFFICE",
  HOME: "HOME",
  CELLPHONE: "CELLPHONE",
} as const;
export type U3120ContactPhoneType = (typeof U3120ContactPhoneType)[keyof typeof U3120ContactPhoneType];

export interface U3120CustomerContact {
  id: string;
  contactName?: string;
  title?: string;
  phone1?: string;
  phone1Type?: U3120ContactPhoneType | null;
  phone1Extension?: string | null;
  phone2?: string;
  phone2Type?: U3120ContactPhoneType | null;
  phone2Extension?: string | null;
  email?: string;
  notes?: string;
  version?: number;
  contactType?: U3120ContactType | null;
}

export interface U3120CustomerEmployeeRate {
  id: string;
  /** Taux horaire ; null si l'utilisateur n'a pas l'action VIEWEMPLOYEERATE (lecture seule API). */
  hourRate?: number | null;
  version?: number;
  /** Date de début de validité (format date-only YYYY-MM-DD). Requis pour le scope Customer. */
  validFrom?: string;
  /** Date de fin de validité (format date-only YYYY-MM-DD). Optionnel. */
  validTo?: string | null;
  employee: {
    id: string;
    codeEmployee?: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface U3120CustomerType {
  id: string;
  customerTypeCode?: string;
  customerTypeNameFr?: string;
  customerTypeNameEn?: string;
}

export interface U3120Customer {
  id: string;
  customerCode: string;
  customerName: string;
  /** Type de client (objet ou id string en state formulaire). */
  customerType?: U3120CustomerType | string | null;
  address?: string;
  city?: string;
  postalCode?: string;
  officePhone1?: string;
  officePhone2?: string;
  isRegularCustomer?: boolean;
  /** Client taxable (défaut métier `true`). Si `false`, aucune taxe n’est applicable côté facturation. */
  taxable?: boolean;
  language?: string;
  country: U3120Country;
  state: U3120State;
  customerContacts?: U3120CustomerContact[];
  customerEmployeeRates?: U3120CustomerEmployeeRate[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export interface U3120Country {
  id: string;
  countryCode?: string;
  countryName?: string;
  countryNameEn?: string;
  countryNameFr?: string;
}

export interface U3120State {
  id: string;
  stateName?: string;
  stateNameEn?: string;
  stateNameFr?: string;
}

export const emptyCustomerContact: U3120CustomerContact = {
  id: "",
  contactName: "",
  title: "",
  phone1: "",
  phone1Type: null,
  phone1Extension: null,
  phone2: "",
  phone2Type: null,
  phone2Extension: null,
  email: "",
  notes: "",
  contactType: null,
};

export const emptyCustomerEmployeeRate: U3120CustomerEmployeeRate = {
  id: "",
  hourRate: 0,
  validFrom: undefined,
  validTo: undefined,
  employee: {
    id: "",
    codeEmployee: "",
    firstName: "",
    lastName: "",
  },
};

export const emptyCustomer: U3120Customer = {
  id: "",
  customerCode: "",
  customerName: "",
  customerType: null,
  address: "",
  city: "",
  postalCode: "",
  officePhone1: "",
  officePhone2: "",
  isRegularCustomer: false,
  taxable: true,
  language: "",
  country: {
    id: "",
    countryCode: "",
    countryName: "",
    countryNameFr: "",
    countryNameEn: "",
  },
  state: {
    id: "",
    stateName: "",
    stateNameFr: "",
    stateNameEn: "",
  },
  customerContacts: [],
  customerEmployeeRates: [],
  createdAt: new Date(),
  updatedAt: new Date(),
  deletedAt: null,
};

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, data: U3120Customer) => {
  return axios.post(LogicalUnitEndpoints.CREATE, data);
};

/** Payload pour création rapide d'un client (depuis U3121). */
export interface U3120QuickCustomerCreatePayload {
  customerName?: string;
  contactName?: string;
  customerType?: U3120CustomerType | string | null;
  address?: string;
  city?: string;
  postalCode?: string;
  officePhone1?: string;
  officePhone1Type?: U3120ContactPhoneType | null;
  officePhone1Extension?: string | null;
  officePhone2?: string;
  officePhone2Type?: U3120ContactPhoneType | null;
  officePhone2Extension?: string | null;
  language?: string;
  notes?: string;
  email?: string;
  isRegularCustomer?: boolean;
  country?: U3120Country | null;
  state?: U3120State | null;
}

export const doCreateQuickCustomer = (axios: AxiosInstance, data: U3120QuickCustomerCreatePayload) => {
  return axios.post(LogicalUnitEndpoints.CREATEQUICKCUSTOMER, data);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, data: U3120Customer) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), data);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, data?: U3120Customer) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), data);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

// IMPORTANT: Les endpoints reçoivent directement un tableau de critères sans wrapper
export const doFindCountries = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => {
  return axios.post(LogicalUnitEndpoints.FINDCOUNTRIES, criterias);
};

// IMPORTANT: Les endpoints reçoivent directement un tableau de critères sans wrapper
export const doFindStates = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => {
  return axios.post(LogicalUnitEndpoints.FINDSTATES, criterias);
};

// IMPORTANT: Les endpoints reçoivent directement un tableau de critères sans wrapper
export const doFindContactTypes = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => {
  return axios.post(LogicalUnitEndpoints.FINDCONTACTTYPES, criterias);
};

// IMPORTANT: Les endpoints reçoivent directement un tableau de critères sans wrapper
export const doFindEmployees = (axios: AxiosInstance, criterias: { fieldName: string; value: unknown }[]) => {
  return axios.post(LogicalUnitEndpoints.FINDEMPLOYEES, criterias);
};

export type U3120AutoCompletCriterias = { fieldName: string; value: unknown }[];

export interface U3120FindCustomerItem {
  id: string;
  customerCode?: string;
  customerName?: string;
}

export const doFindCustomer = (axios: AxiosInstance, criterias: U3120AutoCompletCriterias) => {
  return axios.post(LogicalUnitEndpoints.FINDCUSTOMER, criterias);
};

// API Calls - Export Excel
export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: "fr" | "en") => {
  return axios.post(LogicalUnitEndpoints.EXPORT_EXCEL, { filter, lang });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; errorType?: string };
  switch (apiErr?.errorCode) {
    case 'BU312002':
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errorBU312002', { ns: nameSpace }),
      });
      break;
    case 'BU312004':
      if (apiErr.errorType !== 'TaskNotAllowedForCustomerRateError') {
        errorResult.genericErrors.push({
          title: t('errorTitle', { ns: nameSpace }),
          text: t('errorBU312004InvalidName', { ns: nameSpace }),
        });
      }
      break;
    case 'BU315203':
      errorResult.fieldErrors['contactType.id'] = t('errContactTypeInactive', { ns: nameSpace });
      break;
    default:
      break;
  }
}
